import { useCallback, useEffect, useState } from "react";
import { getAnonId, getHandle } from "./identity.js";

export interface LeaderboardEntry {
  handle: string;
  value: number;
  createdAt: number;
}

export interface SubmitResult {
  ok: true;
  rank: number;
  total: number;
  percentile: number;
}

export type BoardRange = "all" | "today";

interface LeaderboardState {
  entries: LeaderboardEntry[];
  dir: "asc" | "desc";
  loading: boolean;
  error: string | null;
}

/**
 * Fetch + submit against same-origin `/api/*` (no CORS). One hook per game screen.
 * `game` must match a registered GameId server-side.
 *
 * `range` switches between the all-time board and the daily race (UTC day) — the
 * daily board resets every midnight, which is the comeback hook.
 */
export function useLeaderboard(game: string) {
  const [range, setRange] = useState<BoardRange>("all");
  const [state, setState] = useState<LeaderboardState>({
    entries: [],
    dir: "desc",
    loading: true,
    error: null,
  });

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await fetch(
        `/api/leaderboard?game=${encodeURIComponent(game)}&range=${range}`,
      );
      if (!res.ok) throw new Error(`leaderboard ${res.status}`);
      const data = (await res.json()) as {
        entries: LeaderboardEntry[];
        dir: "asc" | "desc";
      };
      setState({
        entries: data.entries,
        dir: data.dir,
        loading: false,
        error: null,
      });
    } catch (e) {
      setState((s) => ({
        ...s,
        loading: false,
        error: e instanceof Error ? e.message : "failed",
      }));
    }
  }, [game, range]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const submit = useCallback(
    async (value: number): Promise<SubmitResult | null> => {
      const handle = getHandle();
      if (handle === null) return null; // caller must collect a handle first
      const res = await fetch("/api/score", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ game, value, handle, anonId: getAnonId() }),
      });
      if (!res.ok) return null;
      const data = (await res.json()) as SubmitResult;
      void refresh();
      return data;
    },
    [game, refresh],
  );

  return { ...state, range, setRange, refresh, submit };
}
